import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  useMediaQuery,
  useTheme,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Autocomplete,
  Snackbar,
  LinearProgress,
} from '@mui/material';
import { httpsCallable } from 'firebase/functions';
import { functions, database, storage } from '../firebase';
import { ref, get, set } from 'firebase/database';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';

interface GuidanceUser {
  uid: string;
  firstName: string;
  lastName: string;
  email: string;
  highSchool: string;
  city: string;
  province: string;
  selfClaimedGuidance: boolean;
}

interface HighSchool {
  id: string;
  name: string;
}

interface ApprovalFormData {
  firstName: string;
  lastName: string;
  email: string;
  username: string;
  highSchool: HighSchool | null;
  avatarFile: File | null;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`guidance-tabpanel-${index}`}
      aria-labelledby={`guidance-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `guidance-tab-${index}`,
    'aria-controls': `guidance-tabpanel-${index}`,
  };
}

const AdminUsers: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pendingUsers, setPendingUsers] = useState<GuidanceUser[]>([]);
  const [activeUsers, setActiveUsers] = useState<GuidanceUser[]>([]);
  
  // Approval modal state
  const [approvalModalOpen, setApprovalModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<GuidanceUser | null>(null);
  const [highSchools, setHighSchools] = useState<HighSchool[]>([]);
  const [loadingHighSchools, setLoadingHighSchools] = useState(false);
  const [formData, setFormData] = useState<ApprovalFormData>({
    firstName: '',
    lastName: '',
    email: '',
    username: '',
    highSchool: null,
    avatarFile: null,
  });
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [successMessage, setSuccessMessage] = useState('');
  const [showSuccessSnackbar, setShowSuccessSnackbar] = useState(false);

  useEffect(() => {
    const fetchUsers = async () => {
      setLoading(true);
      setError(null);

      try {
        // Call the Cloud Function to get guidance users
        const adminGetGuidanceUsers = httpsCallable(functions, 'adminGetGuidanceUsers');
        const result = await adminGetGuidanceUsers();

        const data = result.data as {
          success: boolean;
          pendingUsers: GuidanceUser[];
          activeUsers: GuidanceUser[];
        };

        if (data.success) {
          setPendingUsers(data.pendingUsers || []);
          setActiveUsers(data.activeUsers || []);
        } else {
          setError('Failed to load users. Please try again.');
        }
      } catch (err: any) {
        console.error('Error fetching users:', err);
        setError(err.message || 'Failed to load users. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, []);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  // Fetch high schools for the approval modal
  const fetchHighSchools = async () => {
    setLoadingHighSchools(true);
    try {
      const schoolsRef = ref(database, 'highSchools');
      const snapshot = await get(schoolsRef);
      if (snapshot.exists()) {
        const schoolsData = snapshot.val();
        const schoolsList: HighSchool[] = Object.entries(schoolsData).map(([id, data]: [string, any]) => ({
          id,
          name: data.HighSchoolName || data.SchoolName || '',
        })).filter((school: HighSchool) => school.name) // Filter out schools without names
          .sort((a: HighSchool, b: HighSchool) => a.name.localeCompare(b.name));
        setHighSchools(schoolsList);
      }
    } catch (error) {
      console.error('Error fetching high schools:', error);
    } finally {
      setLoadingHighSchools(false);
    }
  };

  // Handle approve button click
  const handleApproveClick = (user: GuidanceUser) => {
    setSelectedUser(user);
    setFormData({
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      username: `${user.firstName} ${user.lastName}`.trim(),
      highSchool: null,
      avatarFile: null,
    });
    setAvatarPreview(null);
    setApprovalModalOpen(true);
    fetchHighSchools();
  };

  // Handle modal close
  const handleModalClose = () => {
    setApprovalModalOpen(false);
    setSelectedUser(null);
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      username: '',
      highSchool: null,
      avatarFile: null,
    });
    setAvatarPreview(null);
    setUploadProgress(0);
  };

  // Handle avatar file selection
  const handleAvatarChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setError('Please select an image file');
        return;
      }
      // Validate file size (e.g., 5MB max)
      if (file.size > 5 * 1024 * 1024) {
        setError('Image size must be less than 5MB');
        return;
      }
      setFormData({ ...formData, avatarFile: file });
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setAvatarPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  // Handle form submission
  const handleSubmit = async () => {
    if (!selectedUser) return;

    // Validation
    if (!formData.highSchool) {
      setError('Please select a high school');
      return;
    }
    if (!formData.avatarFile) {
      setError('Please select an avatar image');
      return;
    }

    setSubmitting(true);
    setError(null);
    setUploadProgress(0);

    try {
      // Upload avatar to Firebase Storage
      const avatarPath = `schoolUserLogo/hsCounselor_schoolIcon/${selectedUser.uid}.jpg`;
      const avatarStorageRef = storageRef(storage, avatarPath);
      
      // Simulate progress (Firebase Storage doesn't provide progress callbacks in v9)
      const progressInterval = setInterval(() => {
        setUploadProgress((prev) => Math.min(prev + 10, 90));
      }, 200);

      await uploadBytes(avatarStorageRef, formData.avatarFile);
      clearInterval(progressInterval);
      setUploadProgress(90);

      const downloadURL = await getDownloadURL(avatarStorageRef);
      setUploadProgress(100);

      // Create highSchoolUser entry
      const highSchoolUserRef = ref(database, `highSchoolUser/${selectedUser.uid}`);
      await set(highSchoolUserRef, {
        account: formData.email,
        schoolID: formData.highSchool.id,
        schoolIcon: downloadURL,
        schoolName: formData.highSchool.name,
        username: formData.username,
      });

      // Refresh users data
      const adminGetGuidanceUsers = httpsCallable(functions, 'adminGetGuidanceUsers');
      const result = await adminGetGuidanceUsers();
      const data = result.data as {
        success: boolean;
        pendingUsers: GuidanceUser[];
        activeUsers: GuidanceUser[];
      };

      if (data.success) {
        setPendingUsers(data.pendingUsers || []);
        setActiveUsers(data.activeUsers || []);
      }

      setSuccessMessage('Guidance user approved successfully!');
      setShowSuccessSnackbar(true);
      handleModalClose();
      
      // Switch to Active tab to show the newly approved user
      setActiveTab(1);
    } catch (err: any) {
      console.error('Error approving user:', err);
      setError(err.message || 'Failed to approve user. Please try again.');
    } finally {
      setSubmitting(false);
      setUploadProgress(0);
    }
  };

  const renderUserTable = (users: GuidanceUser[], showApproveButton: boolean = false) => {
    if (users.length === 0) {
      return (
        <Alert severity="info">
          No users found in this category.
        </Alert>
      );
    }

    return (
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold', verticalAlign: 'bottom' }}>UID</TableCell>
              <TableCell sx={{ fontWeight: 'bold', verticalAlign: 'bottom' }}>First Name</TableCell>
              <TableCell sx={{ fontWeight: 'bold', verticalAlign: 'bottom' }}>Last Name</TableCell>
              <TableCell sx={{ fontWeight: 'bold', verticalAlign: 'bottom' }}>Email / Phone</TableCell>
              <TableCell sx={{ fontWeight: 'bold', verticalAlign: 'bottom' }}>High School</TableCell>
              <TableCell sx={{ fontWeight: 'bold', verticalAlign: 'bottom' }}>City</TableCell>
              <TableCell sx={{ fontWeight: 'bold', verticalAlign: 'bottom' }}>Province</TableCell>
              <TableCell sx={{ fontWeight: 'bold', verticalAlign: 'bottom' }}>Self Claimed Guidance</TableCell>
              {showApproveButton && <TableCell sx={{ fontWeight: 'bold', verticalAlign: 'bottom' }}></TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((user) => (
              <TableRow key={user.uid} hover>
                <TableCell>
                  <Typography variant="body2" fontFamily="monospace" sx={{ fontSize: '0.75rem' }}>
                    {user.uid}
                  </Typography>
                </TableCell>
                <TableCell>{user.firstName || '-'}</TableCell>
                <TableCell>{user.lastName || '-'}</TableCell>
                <TableCell>{user.email || '-'}</TableCell>
                <TableCell>{user.highSchool || '-'}</TableCell>
                <TableCell>{user.city || '-'}</TableCell>
                <TableCell>{user.province || '-'}</TableCell>
                <TableCell>{user.selfClaimedGuidance ? 'Yes' : 'No'}</TableCell>
                {showApproveButton && (
                  <TableCell>
                    <Button
                      variant="contained"
                      color="primary"
                      size="small"
                      onClick={() => handleApproveClick(user)}
                    >
                      Approve
                    </Button>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  return (
    <Box sx={{ p: isMobile ? 2 : 4 }}>
      <Typography variant="h4" sx={{ mb: 3, fontWeight: 'bold' }}>
        Admin: Users
      </Typography>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : (
        <>
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
            <Tabs value={activeTab} onChange={handleTabChange} aria-label="guidance users tabs">
              <Tab label={`Pending Guidance (${pendingUsers.length})`} {...a11yProps(0)} />
              <Tab label={`Active Guidance (${activeUsers.length})`} {...a11yProps(1)} />
            </Tabs>
          </Box>

          <TabPanel value={activeTab} index={0}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Pending Guidance Users
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Users who set their User Type to Guidance and are not yet setup with a Guidance account.
            </Typography>
            {renderUserTable(pendingUsers, true)}
          </TabPanel>

          <TabPanel value={activeTab} index={1}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Active Guidance Users
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Users with Active Guidance access.
            </Typography>
            {renderUserTable(activeUsers)}
          </TabPanel>
        </>
      )}

      {/* Approval Modal */}
      <Dialog
        open={approvalModalOpen}
        onClose={handleModalClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Approve Guidance User</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {error && (
              <Alert severity="error" onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            <TextField
              label="First Name"
              value={formData.firstName}
              disabled
              fullWidth
            />
            <TextField
              label="Last Name"
              value={formData.lastName}
              disabled
              fullWidth
            />
            <TextField
              label="Email"
              value={formData.email}
              disabled
              fullWidth
            />
            <TextField
              label="Username"
              value={formData.username}
              onChange={(e) => setFormData({ ...formData, username: e.target.value })}
              fullWidth
            />
            <Autocomplete
              options={highSchools}
              getOptionLabel={(option) => option.name}
              loading={loadingHighSchools}
              value={formData.highSchool}
              onChange={(event, newValue) => {
                setFormData({ ...formData, highSchool: newValue });
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="High School"
                  required
                />
              )}
            />
            <Box>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Avatar
              </Typography>
              <input
                accept="image/*"
                style={{ display: 'none' }}
                id="avatar-upload"
                type="file"
                onChange={handleAvatarChange}
              />
              <label htmlFor="avatar-upload">
                <Button variant="outlined" component="span" fullWidth>
                  {formData.avatarFile ? 'Change Avatar' : 'Select Avatar Image'}
                </Button>
              </label>
              {avatarPreview && (
                <Box sx={{ mt: 2, textAlign: 'center' }}>
                  <img
                    src={avatarPreview}
                    alt="Avatar preview"
                    style={{
                      maxWidth: '200px',
                      maxHeight: '200px',
                      objectFit: 'contain',
                      borderRadius: '4px',
                    }}
                  />
                </Box>
              )}
            </Box>
            {submitting && uploadProgress > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Uploading avatar... {uploadProgress}%
                </Typography>
                <LinearProgress variant="determinate" value={uploadProgress} />
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleModalClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={submitting || !formData.highSchool || !formData.avatarFile}
          >
            {submitting ? 'Submitting...' : 'Approve'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Snackbar */}
      <Snackbar
        open={showSuccessSnackbar}
        autoHideDuration={3000}
        onClose={() => setShowSuccessSnackbar(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setShowSuccessSnackbar(false)} severity="success">
          {successMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminUsers;

